import Header from '../../components/public/Header';
import { currentPublicBrand } from '../../lib/publicBrand';
import CareDockLegalPending from './CareDockLegalPending';
import Footer from '../../components/public/Footer';
import { Link } from 'react-router-dom';
import { FileText, Phone, Mail } from 'lucide-react';

const Section = ({ title, children }) => (
  <div>
    <h2 className="font-heading text-xl font-semibold text-text-primary mb-3">{title}</h2>
    <div className="text-text-muted leading-relaxed space-y-3">{children}</div>
    <hr className="border-[#E4E8EB] mt-6" />
  </div>
);

export default function TermsPage() {
  if (currentPublicBrand().isCareDock) return <CareDockLegalPending type="terms" />;

  return (
    <div className="min-h-screen bg-[#F8FAFA]">
      <Header />

      {/* Hero */}
      <section className="bg-white py-16 lg:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-accent rounded-2xl flex items-center justify-center mx-auto mb-6">
            <FileText className="h-8 w-8 text-primary" />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-text-primary mb-4 leading-tight">
            Terms &amp; Conditions
          </h1>
          <p className="text-lg text-text-muted leading-relaxed">
            Please read these terms carefully before using our website or services.
          </p>
          <p className="text-sm text-text-muted mt-4">Last updated: June 2026</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl border border-[#E4E8EB] p-8 space-y-6">

            <Section title="1. About us">
              <p>
                This website is operated by <strong>Osabea Healthcare Solutions Limited</strong>, a company registered in England and Wales.
              </p>
              <p>
                <strong>Registered Office:</strong> Suite FA4D, 1 St Faith's Street, Maidstone, Kent, ME14 1LH<br />
                <strong>CQC Registration:</strong> Provider ID 1-18664797672<br />
                <strong>Contact:</strong> <a href="mailto:info@osabeacares.co.uk" className="text-primary underline">info@osabeacares.co.uk</a> | <a href="tel:01622809469" className="text-primary underline">01622 809469</a>
              </p>
            </Section>

            <Section title="2. Acceptance of terms">
              <p>
                By accessing or using this website, or by requesting any of our services, you agree to be bound by these Terms and Conditions. If you do not agree with any part of these terms, you should not use this website or our services.
              </p>
              <p>
                These terms apply to all users of the website, including without limitation visitors, clients, referrers, job applicants, and workers.
              </p>
            </Section>

            <Section title="3. Our services">
              <p>
                Osabea Healthcare Solutions currently supplies healthcare and support staff to care homes and other care providers. Osabea is also registered with the Care Quality Commission for the regulated activity of Personal care (Provider ID 1-18664797672). Registration is not a statement that every possible direct-care service is currently available.
              </p>
              <p>
                The information on this website is provided for general informational purposes only. It does not constitute medical, legal, financial or professional advice. For care-related decisions, please speak directly with our care team.
              </p>
              <p>
                We reserve the right to withdraw, modify or update our services at any time without notice.
              </p>
            </Section>

            <Section title="4. Care service agreements">
              <p>
                Agency staffing assignments are subject to separately agreed terms with the hiring organisation. Any direct personal-care service offered in future will require confirmation that the service is available and an appropriate separate written agreement with the client or authorised representative. That agreement will govern the relevant service and take precedence over these website Terms and Conditions in the event of a conflict.
              </p>
              <p>
                Sending an enquiry or staffing request does not itself confirm worker availability or create a binding staffing or care-service contract. Any service is subject to acceptance and the applicable written terms.
              </p>
            </Section>

            <Section title="5. Staffing and employment">
              <p>
                Workers placed by Osabea Healthcare Solutions are engaged in accordance with separate written terms of engagement. Care homes and other organisations receiving staffing support operate under a separate written Service Level Agreement.
              </p>
              <p>
                Osabea Healthcare Solutions is responsible for the compliance, training and conduct of all workers it supplies.
              </p>
            </Section>

            <Section title="6. Use of this website">
              <p>You agree to use this website only for lawful purposes and in a way that does not infringe the rights of others or restrict or inhibit their use and enjoyment of the site.</p>
              <p>You must not:</p>
              <ul className="list-disc list-inside space-y-1 mt-1">
                <li>Use the site in a way that violates any applicable law or regulation</li>
                <li>Transmit any unsolicited or unauthorised advertising or promotional material</li>
                <li>Attempt to gain unauthorised access to any part of the website or its related systems</li>
                <li>Use the site to harass, abuse, defame or harm others</li>
                <li>Submit false, misleading or fraudulent information through any form on this site</li>
              </ul>
            </Section>

            <Section title="7. Intellectual property">
              <p>
                All content on this website — including text, graphics, logos, images, and software — is the property of Osabea Healthcare Solutions Limited or its content suppliers and is protected by UK and international copyright law.
              </p>
              <p>
                You may access and view content on this website for personal, non-commercial use only. You must not reproduce, distribute, modify, create derivative works from, or republish any content without our prior written permission.
              </p>
            </Section>

            <Section title="8. Disclaimer and limitation of liability">
              <p>
                The information on this website is provided in good faith but we make no representations or warranties, express or implied, as to its accuracy, completeness or fitness for a particular purpose.
              </p>
              <p>
                To the fullest extent permitted by applicable law, Osabea Healthcare Solutions Limited excludes all liability for any direct, indirect, incidental, or consequential loss or damage arising from your use of this website or reliance on its content.
              </p>
              <p>
                Nothing in these terms limits or excludes our liability for death or personal injury resulting from our negligence, fraud or fraudulent misrepresentation, or any other liability that cannot be limited or excluded under applicable law.
              </p>
            </Section>

            <Section title="9. External links">
              <p>
                This website may contain links to third-party websites. These are provided for your convenience only. We have no control over the content of those sites and accept no responsibility for them or for any loss or damage that may arise from your use of them.
              </p>
            </Section>

            <Section title="10. Complaints">
              <p>
                We take all complaints seriously. If you are unhappy with our website, services or conduct, please contact us directly in the first instance. We have a formal written Complaints Policy which is available upon request and on our <Link to="/complaints" className="text-primary underline">Complaints page</Link>.
              </p>
            </Section>

            <Section title="11. Privacy and data protection">
              <p>
                Our collection and use of your personal data is governed by our <Link to="/privacy" className="text-primary underline">Privacy Policy</Link>, which forms part of these Terms and Conditions. By using this website, you consent to the processing of your data as described therein.
              </p>
            </Section>

            <Section title="12. Changes to these terms">
              <p>
                We may update these Terms and Conditions from time to time. The current version will always be published on this page. Continued use of this website after any changes constitutes your acceptance of the revised terms. We encourage you to review this page periodically.
              </p>
            </Section>

            <Section title="13. Governing law and jurisdiction">
              <p>
                These Terms and Conditions are governed by and construed in accordance with the laws of England and Wales. Any disputes arising in connection with these terms shall be subject to the exclusive jurisdiction of the courts of England and Wales.
              </p>
            </Section>

          </div>

          {/* Contact */}
          <div className="bg-white rounded-2xl border border-[#E4E8EB] p-8 mt-6">
            <h2 className="font-heading text-xl font-semibold text-text-primary mb-4">Questions about these terms?</h2>
            <p className="text-text-muted mb-6">Contact us and we will be happy to explain anything in plain English.</p>
            <div className="space-y-3">
              <a href="mailto:info@osabeacares.co.uk" className="flex items-center gap-4 rounded-xl border border-[#E4E8EB] p-4 bg-[#F8FAFA] hover:border-primary/40 transition-colors">
                <Mail className="h-5 w-5 text-primary flex-shrink-0" />
                <div>
                  <div className="text-xs text-text-muted">Email</div>
                  <div className="font-medium text-text-primary">info@osabeacares.co.uk</div>
                </div>
              </a>
              <a href="tel:01622809469" className="flex items-center gap-4 rounded-xl border border-[#E4E8EB] p-4 bg-[#F8FAFA] hover:border-primary/40 transition-colors">
                <Phone className="h-5 w-5 text-primary flex-shrink-0" />
                <div>
                  <div className="text-xs text-text-muted">Telephone</div>
                  <div className="font-medium text-text-primary">01622 809469</div>
                </div>
              </a>
            </div>
          </div>

        </div>
      </section>

      <Footer />
    </div>
  );
}

